import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  fetchNotifications,
  fetchUnreadCount,
  markNotificationRead,
  markAllNotificationsRead,
} from "../thunks/notifications.thunk";
import { Notification } from "../interfaces/notifications.interface";
import { logout } from "../../auth/slices/auth.slice";
import { logoutUser } from "../../auth/thunks/auth.thunk";

interface NotificationState {
  items: Notification[];
  unreadCount: number;
  loading: boolean;
  error: string | null;
}

const initialState: NotificationState = {
  items: [],
  unreadCount: 0,
  loading: false,
  error: null,
};

const notificationsSlice = createSlice({
  name: "notifications",
  initialState,
  reducers: {
    resetNotifications: () => initialState,
    addNotification: (state, action: PayloadAction<Notification>) => {
      const exists = state.items.some((n) => n.id === action.payload.id);
      if (!exists) {
        state.items = [action.payload, ...state.items];
        state.unreadCount += 1;
      }
    },
    incrementUnreadCount: (state) => {
      state.unreadCount += 1;
    },
    setUnreadCount: (state, action: PayloadAction<number>) => {
      state.unreadCount = action.payload;
    },
    updateNotification: (
      state,
      action: PayloadAction<Partial<Notification> & { id: string }>,
    ) => {
      const index = state.items.findIndex((n) => n.id === action.payload.id);
      if (index !== -1) {
        state.items[index] = { ...state.items[index], ...action.payload };
      }
    },
  },
  extraReducers: (builder) => {
    // Reset on logout (avoid cross-account bleed)
    builder.addCase(logout, () => initialState);
    builder.addCase(logoutUser.fulfilled, () => initialState);
    builder.addCase(logoutUser.rejected, () => initialState);

    // fetchNotifications
    builder.addCase(fetchNotifications.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(fetchNotifications.fulfilled, (state, action) => {
      state.loading = false;
      state.items = action.payload.items || [];
      if (action.payload.unreadCount !== undefined) {
        state.unreadCount = action.payload.unreadCount;
      }
    });
    builder.addCase(fetchNotifications.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload as string;
    });

    // fetchUnreadCount
    builder.addCase(fetchUnreadCount.fulfilled, (state, action) => {
      if (action.payload.unreadCount !== undefined) {
        state.unreadCount = action.payload.unreadCount;
      }
    });

    // markNotificationRead
    builder.addCase(markNotificationRead.fulfilled, (state, action) => {
      const id = action.payload;
      const notification = state.items.find((n) => n.id === id);
      if (notification) {
        if (!notification.isRead) {
          notification.isRead = true;
          state.unreadCount = Math.max(0, state.unreadCount - 1);
        }
      } else {
        // If notification not in current items list (e.g. from Detail page direct link),
        // we still decrement count because the backend successfully marked it as read.
        state.unreadCount = Math.max(0, state.unreadCount - 1);
      }
    });


    // markAllNotificationsRead
    builder.addCase(markAllNotificationsRead.fulfilled, (state) => {
      state.items.forEach((n) => (n.isRead = true));
      state.unreadCount = 0;
    });
  },
});

export const {
  resetNotifications,
  addNotification,
  incrementUnreadCount,
  setUnreadCount,
  updateNotification,
} = notificationsSlice.actions;
export default notificationsSlice.reducer;
